import {
  collection,
  doc,
  query,
  orderBy,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
  type DocumentData,
  type FirestoreDataConverter,
  type WithFieldValue,
} from 'firebase/firestore';

import { firestore } from './config';
import { USE_API, api, apiQuery } from '@/services/api/client';
import type { Reporte, TipoReporte, EstadoReporte } from '@/types/domain';

const conv: FirestoreDataConverter<Reporte> = {
  toFirestore(d: WithFieldValue<Reporte>): DocumentData {
    return d as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<Reporte, 'id'>;
    return { id: snap.id, ...data };
  },
};

export const reportesCollection = collection(firestore, 'reportes').withConverter(conv);

export const reportesQuery: any = USE_API
  ? apiQuery('/reportes', { _order: '-creado_en' })
  : query(reportesCollection, orderBy('creadoEn', 'desc'));

export interface CrearReporteInput {
  tipo: TipoReporte;
  titulo: string;
  descripcion: string;
  imagenes: string[];
  autorUid: string;
  autorNombre: string;
  autorRol?: string;
}

export async function crearReporte(input: CrearReporteInput) {
  if (USE_API) {
    const res = await api.post('/reportes', {
      ...input,
      estado: 'nuevo',
      imagenes: input.imagenes,
    });
    return res.id;
  }
  const id = doc(reportesCollection).id;
  await setDoc(doc(reportesCollection, id), {
    ...input,
    estado: 'nuevo',
    imagenes: input.imagenes ?? [],
    creadoEn: serverTimestamp(),
  } as any);
  return id;
}

export async function actualizarEstadoReporte(id: string, estado: EstadoReporte) {
  if (USE_API) {
    await api.patch(`/reportes/${id}`, { estado });
    return;
  }
  await updateDoc(doc(reportesCollection, id), { estado } as any);
}

/** El admin escribe la solución y marca el reporte como resuelto. */
export async function responderReporte(id: string, solucion: string, resueltoPorNombre: string) {
  if (USE_API) {
    await api.patch(`/reportes/${id}`, { estado: 'resuelto', solucion, resueltoPorNombre });
    return;
  }
  await updateDoc(doc(reportesCollection, id), {
    solucion,
    estado: 'resuelto',
    resueltoPorNombre,
    resueltoEn: serverTimestamp(),
  } as any);
}

export async function eliminarReporte(id: string) {
  if (USE_API) return void (await api.delete(`/reportes/${id}`));
  await deleteDoc(doc(reportesCollection, id));
}
